import { useRef, useState, useEffect } from "react";
import axios from "axios";
import prettyBytes from "pretty-bytes";
import TableContainer from "../../table/TableContainer";
import TableRow from "../../table/TableRow";
import EmptyTableRow from "../../table/EmptyTableRow";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import BrowseIcon from "../../../assets/icons/BrowseIcon";
import useImageCrop from "../../file_upload/useImageCrop";

interface AttachmentsInputProps {
  value?: File[] | (File | string)[];
  error?: boolean;
  multiple?: boolean;
  placeholder?: string;
  accept?: string;
  note?: string;
  cropImage?: {
    title?: string;
    cropSize?: { width: number; height: number };
    previewSize?: { width: number; height: number };
    minZoom?: number;
    maxZoom?: number;
  };
  onChange?: (value: File[]) => void;
  setError?: (message: string) => void;
  onBlur?: () => void;
}

const AttachmentsInput: React.FC<AttachmentsInputProps> = ({
  value = [],
  error,
  multiple = false,
  accept,
  placeholder = "No attachments",
  note,
  cropImage,
  onChange,
  setError,
  onBlur,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [urlInput, setUrlInput] = useState<string>("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);

  const { startCrop, cropElement } = useImageCrop(
    cropImage?.title,
    cropImage?.cropSize,
    cropImage?.previewSize,
    cropImage?.minZoom,
    cropImage?.maxZoom
  );

  // Initialize files from value prop
  useEffect(() => {
    if (Array.isArray(value)) {
      const fileArray = value.filter((item): item is File => item instanceof File);
      setFiles(fileArray);
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);

      if (cropImage && accept?.startsWith("image/") && newFiles.length === 1) {
        startCrop(newFiles[0], (croppedImage) => {
          const croppedFile = new File([croppedImage], newFiles[0].name, {
            type: newFiles[0].type,
          });
          const updatedFiles = [...files, croppedFile];
          setFiles(updatedFiles);
          onChange?.(updatedFiles);
        });
      } else {
        const updatedFiles = [...files, ...newFiles];
        setFiles(updatedFiles);
        onChange?.(updatedFiles);
      }
    }
  };

  const handleURLUpload = async () => {
    if (!urlInput.trim()) {
      alert("Please enter a valid URL.");
      return;
    }
    try {
      console.log("Fetching file from:", urlInput);
      const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(urlInput)}`;
      const response = await axios.get(proxyUrl, { responseType: "blob" });

      const fileName = urlInput.split("/").pop() || "downloaded-file";

      if (cropImage && response.data.type.startsWith("image/")) {
        const objectUrl = URL.createObjectURL(response.data);
        setPreviewUrl(objectUrl);
        startCrop(
          new File([response.data], fileName, { type: response.data.type }),
          (croppedImage) => {
            const croppedFile = new File([croppedImage], fileName, {
              type: response.data.type,
            });
            const updatedFiles = [...files, croppedFile];
            setFiles(updatedFiles);
            onChange?.(updatedFiles);
            setUrlInput("");
            setPreviewUrl(null);
          }
        );
      } else {
        const file = new File([response.data], fileName, {
          type: response.data.type,
        });
        const objectUrl = URL.createObjectURL(response.data);
        setPreviewUrl(objectUrl);
        const updatedFiles = [...files, file];
        setFiles(updatedFiles);
        onChange?.(updatedFiles);
        setUrlInput("");
        alert("🎉 File uploaded successfully!");
      }
    } catch (error) {
      console.error("Upload Error:", error);
      alert("Failed to upload the file. Check console for details.");
    }
  };

  const handleDelete = (fileName: string) => {
    const updatedFiles = files.filter(file => file.name !== fileName);
    setFiles(updatedFiles);
    onChange?.(updatedFiles);
    setPreviewUrl(null);
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-sm">
      {cropElement}
      <div className="border-2 border-dashed border-blue-500 rounded-lg py-20 mb-4 text-center">
        <BrowseIcon className="w-12 h-12 mx-auto text-blue-500" />
        <p className="text-gray-500">
          Drag your file(s) or{" "}
          <span
            className="text-blue-500 underline cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            browse
          </span>
        </p>
        <p className="text-xs text-gray-400">Max 10 MB files are allowed</p>
        <input
          type="file"
          ref={fileInputRef}
          accept={accept}
          multiple={multiple}
          className="hidden"
          onChange={handleFileChange}
          onBlur={onBlur}
        />
      </div>
      <div className="text-sm text-ctb-grey-300">{note}</div>
      <div className="flex items-center my-4">
        <div className="flex-grow border-t border-gray-300"></div>
        <span className="px-4 text-gray-500 text-sm">OR</span>
        <div className="flex-grow border-t border-gray-300"></div>
      </div>
      <div className="flex items-center mb-6">
        <input
          type="text"
          placeholder="Enter file URL"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          className="flex-grow border border-gray-300 me-4 rounded-xl p-4 bg-ctb-grey-400 text-sm"
        />
        <button
          type="button"
          onClick={handleURLUpload}
          className="px-4 py-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
        >
          Upload
        </button>
      </div>
      {previewUrl && (
        <div className="mb-6">
          <h3 className="text-gray-600 text-sm mb-2">File Preview:</h3>
          <iframe
            src={previewUrl}
            width="100%"
            height="400px"
            className="border rounded-lg shadow"
            title="File preview"
          />
        </div>
      )}
      <TableContainer columns={[{ name: "Filename" }, { name: "Size" }]} className="relative">
        {files.length > 0 ? (
          files.map((file, index) => (
            <TableRow key={index} className={index !== files.length - 1 ? "border-b" : ""}>
              <p className="truncate max-w-xs overflow-hidden text-ellipsis">
                {file.name}
              </p>
              <p>{prettyBytes(file.size)}</p>
              <button
                type="button"
                className="absolute right-9 stroke-red-500"
                onClick={() => handleDelete(file.name)}
              >
                <DeleteIcon className="stroke-inherit" />
              </button>
            </TableRow>
          ))
        ) : (
          <EmptyTableRow message={placeholder || "No attachments"} />
        )}
      </TableContainer>
    </div>
  );
};

export default AttachmentsInput;


